import { ArrowRight } from 'lucide-react';
import type { Locale } from '@/lib/i18n/config';
import type { Dictionary } from '@/lib/i18n/dictionaries';
import type { TeamMember } from '@/lib/supabase/types';
import { localeField } from '@/lib/i18n/helpers';
import { Reveal } from '@/components/site/reveal';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

const fallbackPhotos = [
  'https://images.pexels.com/photos/5207262/pexels-photo-5207262.jpeg?auto=compress&cs=tinysrgb&w=600',
  'https://images.pexels.com/photos/5215024/pexels-photo-5215024.jpeg?auto=compress&cs=tinysrgb&w=600',
  'https://images.pexels.com/photos/8442097/pexels-photo-8442097.jpeg?auto=compress&cs=tinysrgb&w=600',
  'https://images.pexels.com/photos/5212340/pexels-photo-5212340.jpeg?auto=compress&cs=tinysrgb&w=600',
];

export function TeamSection({
  locale,
  dict,
  team,
  compact = false,
}: {
  locale: Locale;
  dict: Dictionary;
  team: TeamMember[];
  compact?: boolean;
}) {
  return (
    <section id="equipo" className="section-pad bg-secondary/50">
      <div className="container-tight">
        {!compact && (
          <div className="mb-10 flex items-start gap-3 sm:mb-12 sm:items-center sm:gap-4">
            <span className="shrink-0 font-serif text-3xl font-bold leading-none text-accent/20 sm:text-5xl">{dict.team.sectionTitle}</span>
            <div className="min-w-0 max-w-2xl">
              <span className="eyebrow">{dict.team.title}</span>
              <h2 className="mt-1 font-serif text-2xl font-bold text-primary sm:text-3xl md:text-4xl">{dict.team.title}</h2>
              <p className="mt-2 text-base text-muted-foreground sm:text-lg">{dict.team.subtitle}</p>
            </div>
          </div>
        )}

        <div className="flex flex-wrap justify-center gap-6">
          {team.map((m, i) => (
            <Reveal
              key={m.id}
              delay={i * 90}
              as="article"
              className="w-full sm:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)]"
            >
              <TeamCard
                member={m}
                photo={m.photo_url || fallbackPhotos[i % fallbackPhotos.length]}
                locale={locale}
                dict={dict}
              />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function TeamCard({
  member,
  photo,
  locale,
  dict,
}: {
  member: TeamMember;
  photo: string;
  locale: Locale;
  dict: Dictionary;
}) {
  const isAvatar = photo.startsWith('/avatar-');
  const role = localeField(member, 'role', locale);
  const bio = localeField(member, 'bio', locale).trim();

  const card = (
    <>
      <div className="relative aspect-[4/5] overflow-hidden bg-muted">
        <img
          src={photo}
          alt={member.full_name}
          className={
            isAvatar
              ? 'h-full w-full object-contain p-3 transition-transform duration-700 ease-out group-hover:scale-105'
              : 'h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110'
          }
        />
        {/* Gradient veil at the bottom of the photo for legibility on hover */}
        {!isAvatar && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-primary/60 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
        )}
      </div>
      <div className="p-5">
        <h3 className="font-semibold text-primary transition-colors group-hover:text-accent">{member.full_name}</h3>
        <p className="mt-0.5 text-sm text-accent">{role}</p>
        {member.areas && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {member.areas.split(',').map((area) => (
              <span key={area} className="rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-medium text-accent transition-colors group-hover:bg-accent group-hover:text-accent-foreground">
                {area.trim()}
              </span>
            ))}
          </div>
        )}
        {bio && (
          <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-accent">
            {dict.team.viewBio}
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        )}
      </div>
    </>
  );

  if (!bio) {
    return <div className="card-surface group flex h-full flex-col overflow-hidden">{card}</div>;
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="card-surface group flex h-full w-full flex-col overflow-hidden text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
        >
          {card}
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-4 text-left">
            <img
              src={photo}
              alt={member.full_name}
              className={
                isAvatar
                  ? 'h-16 w-16 shrink-0 rounded-full bg-muted object-contain p-1'
                  : 'h-16 w-16 shrink-0 rounded-full object-cover'
              }
            />
            <div className="min-w-0">
              <DialogTitle className="font-serif text-primary">{member.full_name}</DialogTitle>
              <DialogDescription className="text-accent">{role}</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        {member.areas && (
          <div className="flex flex-wrap gap-1.5">
            {member.areas.split(',').map((area) => (
              <span key={area} className="rounded-full bg-accent/10 px-2 py-0.5 text-[10px] font-medium text-accent">
                {area.trim()}
              </span>
            ))}
          </div>
        )}
        <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">
          {bio.split('\n\n').map((para, idx) => (
            <p key={idx}>{para}</p>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
